import { useEffect, useRef, useState } from "react";
import SpeechRecognition, {
  useSpeechRecognition
} from "react-speech-recognition";

import { playStartSound, playStopSound } from "../utils/audioNotifications";

interface UseAudioRecordingOptions {
  /** BCP-47 language tag passed to SpeechRecognition (default: 'ro-RO') */
  language?: string;
  /** Play start/stop audio feedback (default: true) */
  sounds?: boolean;
}

export const useAudioRecording = ({
  language = "ro-RO",
  sounds = true
}: UseAudioRecordingOptions = {}) => {
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
    isMicrophoneAvailable
  } = useSpeechRecognition();

  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<NodeJS.Timeout>();
  const silenceTimeoutRef = useRef<NodeJS.Timeout>();
  const totalTimeoutRef = useRef<NodeJS.Timeout>();
  const lastTranscriptRef = useRef<string>("");

  // Tracks whether we intentionally want to keep recording.
  // Must be set to false BEFORE calling stopListening so the
  // auto-restart effect below does not immediately restart.
  const isActiveRef = useRef<boolean>(false);

  // Accumulates transcript segments across Android sub-sessions.
  // Android ends each recognition session after a short pause, so we
  // stitch together the pieces here instead of relying on continuous mode.
  const accumulatedRef = useRef<string>("");

  // Mirrors the latest transcript value so the auto-restart effect can read it
  // without adding transcript to its dep array (which would cause double-starts).
  const latestTranscriptRef = useRef<string>("");

  // Full transcript = text from all finished sub-sessions + current session
  const fullTranscript = [accumulatedRef.current, transcript]
    .filter(Boolean)
    .join(" ")
    .trim();

  // Start/stop recording timer
  useEffect(() => {
    if (listening) {
      setRecordingTime(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      setRecordingTime(0);
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [listening]);

  useEffect(() => {
    latestTranscriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    if (!listening && isActiveRef.current) {
      if (latestTranscriptRef.current) {
        accumulatedRef.current = [
          accumulatedRef.current,
          latestTranscriptRef.current
        ]
          .filter(Boolean)
          .join(" ")
          .trim();
      }
      resetTranscript();
      SpeechRecognition.startListening({ continuous: false, language });
    }
  }, [listening, language, resetTranscript]);

  // Handle silence / total auto-stop
  useEffect(() => {
    if (!listening) {
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
      return;
    }

    const currentFull = [accumulatedRef.current, transcript]
      .filter(Boolean)
      .join(" ")
      .trim();

    if (currentFull !== lastTranscriptRef.current) {
      lastTranscriptRef.current = currentFull;
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = setTimeout(() => {
        isActiveRef.current = false;
        SpeechRecognition.stopListening();
        if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
        totalTimeoutRef.current = undefined;
      }, 4000); // 4 s silence
    }

    if (!totalTimeoutRef.current) {
      totalTimeoutRef.current = setTimeout(() => {
        isActiveRef.current = false;
        SpeechRecognition.stopListening();
        if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = undefined;
      }, 7000); // 7 s total
    }

    return () => {
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    };
  }, [listening, transcript]);

  const toggleMicrophone = () => {
    if (!browserSupportsSpeechRecognition) return;

    if (listening || isActiveRef.current) {
      isActiveRef.current = false;
      if (sounds) playStopSound();
      SpeechRecognition.stopListening();
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
      totalTimeoutRef.current = undefined;
    } else {
      if (sounds) playStartSound();
      accumulatedRef.current = "";
      lastTranscriptRef.current = "";
      if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
      if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
      totalTimeoutRef.current = undefined;
      resetTranscript();
      isActiveRef.current = true;
      SpeechRecognition.startListening({ continuous: false, language });
    }
  };

  const stopRecording = (abort = false) => {
    isActiveRef.current = false;
    if (listening) {
      if (abort) {
        SpeechRecognition.abortListening();
      } else {
        SpeechRecognition.stopListening();
      }
    }
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    if (totalTimeoutRef.current) clearTimeout(totalTimeoutRef.current);
    totalTimeoutRef.current = undefined;
    accumulatedRef.current = "";
    resetTranscript();
  };

  const formatRecordingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return {
    transcript: fullTranscript,
    listening: listening || isActiveRef.current,
    isMicrophoneAvailable,
    resetTranscript: () => {
      accumulatedRef.current = "";
      resetTranscript();
    },
    recordingTime,
    formattedTime: formatRecordingTime(recordingTime),
    toggleMicrophone,
    stopRecording,
    browserSupportsSpeechRecognition
  };
};
